import type { CSSProperties, MouseEvent, PointerEvent, ReactNode } from "react";
import {
  ArrowRightIcon,
  CheckIcon,
  InfoIcon,
  Loader2Icon,
  TriangleAlertIcon,
  XIcon,
} from "@/components/icons/huge-lucide";
import { toast as sonnerToast, type ExternalToast } from "sonner";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

type ToastVariant =
  | "default"
  | "success"
  | "info"
  | "warning"
  | "error"
  | "loading";

export type ToastLink = {
  href: string;
  label: string;
  target?: "_self" | "_blank";
  rel?: string;
};

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type AppToastOptions = Omit<
  ExternalToast,
  | "action"
  | "cancel"
  | "description"
  | "className"
  | "classNames"
  | "icon"
  | "style"
> & {
  description?: ReactNode;
  link?: ToastLink;
  action?: ToastAction;
};

type AppToastProps = {
  id: number | string;
  title: ReactNode;
  variant: ToastVariant;
  description?: ReactNode;
  link?: ToastLink;
  action?: ToastAction;
  countdownMs?: number | null;
};

function buildToastId(): string {
  return `furl-toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── Per-variant accent tokens ────────────────────────────────────────────────

const VARIANT_VARS: Record<ToastVariant, CSSProperties> = {
  default: {
    "--t-accent": "var(--brand)",
    "--t-accent-dim": "var(--brand-dim)",
    "--t-accent-border": "var(--brand-border)",
  } as CSSProperties,
  success: {
    "--t-accent": "var(--green)",
    "--t-accent-dim": "var(--green-bg)",
    "--t-accent-border": "var(--green-deep)",
  } as CSSProperties,
  info: {
    "--t-accent": "var(--colors-sky-blue-500, hsl(199 89% 48%))",
    "--t-accent-dim":
      "color-mix(in srgb, var(--colors-sky-blue-500, hsl(199 89% 48%)) 14%, transparent)",
    "--t-accent-border":
      "color-mix(in srgb, var(--colors-sky-blue-500, hsl(199 89% 48%)) 30%, transparent)",
  } as CSSProperties,
  warning: {
    "--t-accent": "var(--amber)",
    "--t-accent-dim": "var(--amber-dim)",
    "--t-accent-border": "var(--amber-border)",
  } as CSSProperties,
  error: {
    "--t-accent": "var(--warn)",
    "--t-accent-dim": "var(--warn-dim)",
    "--t-accent-border": "var(--warn-border)",
  } as CSSProperties,
  loading: {
    "--t-accent": "var(--accent-purple, hsl(262 80% 65%))",
    "--t-accent-dim":
      "var(--accent-dim, color-mix(in srgb, hsl(262 80% 65%) 14%, transparent))",
    "--t-accent-border":
      "var(--accent-border, color-mix(in srgb, hsl(262 80% 65%) 30%, transparent))",
  } as CSSProperties,
};

// ─── Icon ─────────────────────────────────────────────────────────────────────

function ToastIcon({ variant }: { variant: ToastVariant }) {
  const cls = "h-4 w-4";
  switch (variant) {
    case "success":
      return <CheckIcon className={cn(cls, "text-foreground")} />;
    case "error":
      return <XIcon className={cls} />;
    case "warning":
      return <TriangleAlertIcon className={cls} />;
    case "loading":
      return <Loader2Icon className={`${cls} animate-spin`} />;
    default:
      return <InfoIcon className={cls} />;
  }
}

// ─── Action link / button ─────────────────────────────────────────────────────

function ToastActionLink({ link }: { link: ToastLink }) {
  const target = link.target ?? "_blank";
  const rel =
    link.rel ??
    (target === "_blank" ? "noopener noreferrer nofollow" : undefined);
  return (
    <a
      href={link.href}
      target={target}
      rel={rel}
      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold transition-colors"
      style={{ color: "var(--t-accent)" }}
    >
      {link.label}
      <ArrowRightIcon className="h-3 w-3" />
    </a>
  );
}

function ToastActionButton({
  action,
  id,
}: {
  action: ToastAction;
  id: number | string;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        action.onClick();
        dismissToast(id);
      }}
      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold transition-colors"
      style={{ color: "var(--t-accent)" }}
    >
      {action.label}
      <ArrowRightIcon className="h-3 w-3" />
    </button>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function AppToastCard({
  id,
  title,
  variant,
  description,
  link,
  action,
  countdownMs,
}: AppToastProps) {
  const isLoading = variant === "loading";
  const hasCountdown =
    !isLoading && typeof countdownMs === "number" && countdownMs > 0;

  const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dismissToast(id);
  };

  const handleDismissPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    // Prevent parent swipe/drag handlers from swallowing the click action.
    event.stopPropagation();
  };

  return (
    // w-85 max-w-[calc(100vw-2rem)]
    <div
      className="relative  rounded-2xl border shadow-2xl overflow-hidden"
      style={{
        ...VARIANT_VARS[variant],
        background: "var(--card, hsl(220 13% 9%))",
        borderColor: "var(--border, hsl(220 13% 20%))",
      }}
    >
      {/* ── Progress bar ─────────────────────────────────────────────────── */}
      <div
        className="absolute top-0 left-0 h-0.5 w-full"
        style={{ background: "var(--border, hsl(220 13% 16%))" }}
        aria-hidden="true"
      >
        <div
          className={`h-full rounded-full transition-[width] ${
            isLoading ? "animate-pulse" : ""
          }`}
          style={{
            width: isLoading ? "55%" : hasCountdown ? "100%" : "72%",
            background: "var(--t-accent)",
            animation: hasCountdown
              ? `furl-toast-countdown ${countdownMs}ms linear forwards`
              : undefined,
          }}
        />
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3.5">
        {/* Icon badge */}
        <div
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border"
          style={{
            color: "var(--t-accent)",
            background: "var(--t-accent-dim)",
            borderColor: "var(--t-accent-border)",
          }}
          aria-hidden="true"
        >
          <ToastIcon variant={variant} />
        </div>

        {/* Text + action */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-[12px] font-semibold leading-tight text-foreground">
            {title}
          </p>
          {description && (
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
          {action ? <ToastActionButton action={action} id={id} /> : null}
          {!action && link ? <ToastActionLink link={link} /> : null}
        </div>

        {/* Close button — no stopPropagation; pointer-events handled by wrapper */}
        <button
          type="button"
          aria-label="Dismiss notification"
          onPointerDown={handleDismissPointerDown}
          onClick={handleDismiss}
          className="relative z-10 pointer-events-auto mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-muted-foreground/60 hover:text-foreground transition-colors"
        >
          <XIcon className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ─── Public API ───────────────────────────────────────────────────────────────

function showToast(
  variant: ToastVariant,
  title: ReactNode,
  options?: AppToastOptions,
) {
  const {
    description,
    link,
    action,
    duration,
    id,
    toasterId,
    invert,
    position,
    dismissible,
    onDismiss,
    onAutoClose,
    testId,
  } = options ?? {};

  const resolvedDuration = resolveDurationMs(variant, duration);
  const countdownMs =
    typeof resolvedDuration === "number" && Number.isFinite(resolvedDuration)
      ? resolvedDuration
      : null;
  const resolvedId = id ?? buildToastId();

  return sonnerToast.custom(
    () => (
      <AppToastCard
        id={resolvedId}
        title={title}
        variant={variant}
        description={description}
        link={link}
        action={action}
        countdownMs={countdownMs}
      />
    ),
    {
      id: resolvedId,
      toasterId,
      invert,
      position,
      dismissible: dismissible ?? true,
      onDismiss,
      onAutoClose,
      testId,
      duration: resolvedDuration,
      // pointer-events-auto! ensures sonner's li wrapper never blocks clicks
      // even if sonner's own stylesheet sets pointer-events: none on the element.
      className:
        "p-0! bg-transparent! border-0! shadow-none! pointer-events-auto!",
      style: {
        padding: 0,
        background: "transparent",
        border: "none",
        boxShadow: "none",
        pointerEvents: "auto",
      },
    },
  );
}

function dismissToast(id: number | string) {
  sonnerToast.dismiss(id);
}

function isLoading(variant: ToastVariant): boolean {
  return variant === "loading";
}

function resolveDurationMs(
  variant: ToastVariant,
  duration: number | undefined,
): number {
  if (isLoading(variant)) {
    // Loading toasts are lifecycle-driven and should close when callers dismiss
    // them (typically when a promise resolves), not on an arbitrary timer.
    return Infinity;
  }
  if (typeof duration === "number" && Number.isFinite(duration)) {
    return duration;
  }
  return 4000;
}

export const toast = Object.assign(
  (message: ReactNode, options?: AppToastOptions) =>
    showToast("default", message, options),
  {
    message: (message: ReactNode, options?: AppToastOptions) =>
      showToast("default", message, options),
    success: (message: ReactNode, options?: AppToastOptions) =>
      showToast("success", message, options),
    info: (message: ReactNode, options?: AppToastOptions) =>
      showToast("info", message, options),
    warning: (message: ReactNode, options?: AppToastOptions) =>
      showToast("warning", message, options),
    error: (message: ReactNode, options?: AppToastOptions) =>
      showToast("error", message, options),
    loading: (message: ReactNode, options?: AppToastOptions) =>
      showToast("loading", message, options),
    dismiss: sonnerToast.dismiss,
    promise: sonnerToast.promise,
    custom: sonnerToast.custom,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
  },
);
